import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    FormControlLabel,
    Checkbox,
    CircularProgress,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { AuthService } from '../services/api/authService';
import { LoginRequest } from '../types/api.types';
import loginImgFrame from '../assets/login-img-frame.svg';
import moneyTooLogo from '../assets/money-too-logo.svg';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [formData, setFormData] = useState<LoginRequest>({
        email: '',
        password: '',
    });
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when user starts typing
        if (error) setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await AuthService.login(formData);
            if (response.token && response.user) {
                login(response.token, response.user);
                // Redirect users to appropriate page based on their role
                if (response.user.role.name === 'User') {
                    navigate('/portfolio');
                } else {
                    navigate('/');
                }
            } else {
                let errorMessage = 'Login failed. Please try again.';
                setError(errorMessage);
            }
        } catch (err: any) {
            let errorMessage = 'Login failed. Please try again.';
            if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            } else if (err.response?.status === 401) {
                errorMessage = 'Invalid email or password';
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Background Stars */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '10%',
                        left: '10%',
                        width: '4px',
                        height: '4px',
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '50%',
                        boxShadow: `
                            20px 30px 0 rgba(255,255,255,0.2),
                            40px 70px 0 rgba(255,255,255,0.1),
                            90px 40px 0 rgba(255,255,255,0.3),
                            130px 80px 0 rgba(255,255,255,0.2),
                            160px 30px 0 rgba(255,255,255,0.1),
                            200px 60px 0 rgba(255,255,255,0.2),
                            240px 90px 0 rgba(255,255,255,0.3),
                            280px 10px 0 rgba(255,255,255,0.1),
                            320px 50px 0 rgba(255,255,255,0.2),
                            360px 80px 0 rgba(255,255,255,0.1)
                        `,
                    },
                }}
            />

            <Box 
                sx={{ 
                    display: 'flex', 
                    minHeight: '100vh',
                    flexDirection: { xs: 'column', md: 'row' }
                }}
            >
                {/* Left side - Logo and branding */}
                <Box
                    sx={{
                        flex: { xs: 'none', md: '1 1 50%' },
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        height: '100vh',
                        width: '100%',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '300px',
                            height: '300px',
                            background: 'radial-gradient(circle, rgba(255, 182, 193, 0.15) 0%, rgba(255, 182, 193, 0.05) 50%, transparent 70%)',
                            borderRadius: '50%',
                            filter: 'blur(20px)',
                            zIndex: 1,
                        },
                    }}
                >
                    <Box sx={{ 
                        position: 'relative', 
                        zIndex: 2,
                        width: '100%',
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <img 
                            src={loginImgFrame} 
                            alt="Login frame" 
                            style={{ 
                                width: '100%',
                                height: '100vh',
                                objectFit: 'cover',
                                objectPosition: 'center',
                            }} 
                        />
                    </Box>
                </Box>

                {/* Right side - Login form with WHITE background */}
                <Box
                    sx={{
                        flex: { xs: 'none', md: '1 1 50%' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'white',
                        minHeight: { xs: '50vh', md: '100vh' },
                        p: { xs: 2, md: 4 },
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: 4 },
                            width: '100%',
                            maxWidth: { xs: '100%', md: '90%', lg: '85%' },
                            background: 'white',
                            borderRadius: 3,
                            boxShadow: 'none',
                        }}
                    >
                        {/* Mobile Logo - Only visible on small screens */}
                        <Box sx={{ textAlign: 'center', mb: 3, display: { xs: 'flex', md: 'none' }, flexDirection: 'column', alignItems: 'center' }}>
                            <img 
                                src={moneyTooLogo} 
                                alt="MoneyToo! Logo" 
                                width="80" 
                                height="43" 
                                style={{ 
                                    marginBottom: 16,
                                }} 
                            />
                        </Box>

                        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <img 
                                src={moneyTooLogo} 
                                alt="MoneyToo! Logo" 
                                width="46" 
                                height="25" 
                                style={{ 
                                    marginBottom: 16,
                                }} 
                            />
                            <Typography 
                                variant="h5" 
                                component="h1" 
                                gutterBottom 
                                sx={{ 
                                    color: '#18010a', 
                                    fontWeight: 600,
                                    fontSize: { xs: '1.5rem', sm: '2rem' },
                                    textAlign: 'left',
                                }}
                            >
                                Welcome to MoneyToo!
                            </Typography>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                    fontSize: { xs: '0.875rem', sm: '0.9rem' },
                                    textAlign: 'left',
                                }}
                            >
                                Enter your credentials to explore the interface
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    mb: 1, 
                                    color: '#0a0a0a',
                                    fontWeight: 500,
                                }}
                            >
                                Email
                            </Typography>
                            <TextField
                                fullWidth
                                name="email"
                                type="email"
                                variant="outlined"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                                sx={{ mb: 3 }}
                                placeholder="hi@example.com"
                                InputProps={{
                                    sx: {
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#E6E6E9',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#760731',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#760731',
                                            },
                                        },
                                    },
                                }}
                            />

                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    mb: 1, 
                                    color: '#0a0a0a',
                                    fontWeight: 500,
                                }}
                            >
                                Password
                            </Typography>
                            <TextField
                                fullWidth
                                name="password"
                                type="password"
                                variant="outlined"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                                sx={{ mb: 2 }}
                                placeholder="••••••••••••"
                                InputProps={{
                                    sx: {
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#e0e0e0',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#760731',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#760731',
                                            },
                                        },
                                    },
                                }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        sx={{
                                            color: '#760731',
                                            '&.Mui-checked': {
                                                color: '#760731',
                                            },
                                        }}
                                        size='small'
                                        disabled={loading}
                                    />
                                }
                                label="Remember Me"
                                sx={{ mb: 3,"& .MuiFormControlLabel-label": { fontSize: '0.8rem' } }}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    py: 1.5,
                                    bgcolor: '#760731',
                                    '&:hover': {
                                        bgcolor: '#5a0524',
                                    },
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Login'
                                )}
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        </div>
    );
};

export default LoginPage;